import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import HibaGomb from '../hibajelentes/HibaGomb.tsx';
import { supabase } from '../lib/supabase.ts';
import { Gomb, HibaUzenet, Parbeszed, Valaszto } from '../ui/elemek.tsx';

interface Menu { ut: string; cim: string; jelveny?: number; jelvenyLeiras?: string }

/** A monogram a névből (vagy e-mailből): legfeljebb két kezdőbetű. */
function monogram(szoveg: string): string {
  const reszek = szoveg.split(/[\s@.]+/).filter(Boolean);
  return reszek.slice(0, 2).map((r) => r[0]!.toUpperCase()).join('');
}

const SIN_LINK = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-10 items-center justify-between rounded-[10px] px-3 text-sm transition ${
    isActive ? 'bg-felulet text-szoveg shadow-[inset_2px_0_0_var(--szin-kiemelt)]' : 'text-szoveg-halk hover:bg-felulet hover:text-szoveg'}`;

const ALSO_LINK = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-11 items-center justify-center rounded-[10px] px-1 text-xs transition ${isActive ? 'bg-felulet text-szoveg' : 'text-szoveg-halk'}`;

/** A menüpont mellett a darabszám-jelvény (csak pozitív számnál). */
function Jelveny({ menu }: { menu: Menu }) {
  if (menu.jelveny === undefined || menu.jelveny <= 0) return null;
  return (
    <em className="szam rounded-full bg-kiemelt px-[7px] py-px text-xs font-semibold text-kiemelt-szoveg not-italic">
      {menu.jelveny}<span className="sr-only"> {menu.jelvenyLeiras ?? ''}</span>
    </em>
  );
}

export default function Keret() {
  const { profil, kijelentkezes } = useAuth();
  const { irodak, aktivId, beallit, jogok } = useAktivIroda();
  const [kilepesHiba, setKilepesHiba] = useState<unknown>(null);
  const [tobbNyitva, setTobbNyitva] = useState(false);
  const kilep = () => { setKilepesHiba(null); kijelentkezes().catch(setKilepesHiba); };

  // Az aktív iroda új leadjeinek száma a „Leadek” menüpont mellett (percenként frissül; a státuszváltások is érvénytelenítik).
  const ujLeadek = useQuery({
    queryKey: ['leadek', 'uj-jelveny', aktivId],
    enabled: aktivId !== null,
    queryFn: async () => {
      const { count, error } = await supabase.from('leads').select('id', { count: 'exact', head: true }).eq('office_id', aktivId!).eq('statusz', 'uj');
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // A függő iroda-regisztrációk száma a super admin „Regisztrációk” menüpontja mellett.
  const fuggoRegisztraciok = useQuery({
    queryKey: ['super-regisztraciok', 'jelveny'],
    enabled: jogok.super,
    queryFn: async () => {
      const { count, error } = await supabase.from('iroda_regisztraciok').select('id', { count: 'exact', head: true }).eq('allapot', 'fuggo');
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // A beküldött (még senki által nem vizsgált) hibajelentések száma a super admin „Hibajelentések” menüpontja mellett.
  const ujHibajegyek = useQuery({
    queryKey: ['hibajegyek', 'jelveny'],
    enabled: jogok.super,
    queryFn: async () => {
      const { count, error } = await supabase.from('hibajegyek').select('id', { count: 'exact', head: true }).eq('statusz', 'bekuldve');
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const fo: Menu[] = [
    { ut: '/leadek', cim: 'Leadek', jelveny: ujLeadek.data, jelvenyLeiras: 'új' },
    { ut: '/elemzes', cim: 'Elemzés' },
    { ut: '/hibajelentesek', cim: 'Hibajelentések', jelveny: jogok.super ? ujHibajegyek.data : undefined, jelvenyLeiras: 'beküldött' },
    ...(jogok.vezeto ? [{ ut: '/kollegak', cim: 'Kollégák' }, { ut: '/weboldal', cim: 'Weboldal' }] : []),
  ];
  const uzemeltetes: Menu[] = jogok.super
    ? [{ ut: '/super/regisztraciok', cim: 'Regisztrációk', jelveny: fuggoRegisztraciok.data, jelvenyLeiras: 'függő' }, { ut: '/super/irodak', cim: 'Irodák' }, { ut: '/super/felhasznalok', cim: 'Felhasználók' }, { ut: '/super/minoseg', cim: 'Minőség' }, { ut: '/super/outbox', cim: 'Kiküldések' }]
    : [];
  const nev = profil?.nev || profil?.email || '';
  const szerep = jogok.super ? 'super admin' : jogok.vezeto ? 'irodavezető' : 'értékesítő';

  // Mobil: Leadek, Elemzés, Kollégák, Profil; a többi (Hibajelentések, Weboldal, üzemeltetés) a „Több” lapról érhető el.
  const mobilFo: Menu[] = [
    { ut: '/leadek', cim: 'Leadek' }, { ut: '/elemzes', cim: 'Elemzés' },
    ...(jogok.vezeto ? [{ ut: '/kollegak', cim: 'Kollégák' }] : []),
  ];
  const tobb: Menu[] = [fo.find((m) => m.ut === '/hibajelentesek')!, ...(jogok.vezeto ? [{ ut: '/weboldal', cim: 'Weboldal' }] : []), ...uzemeltetes];
  const iroda = irodak.length > 1 && aktivId
    ? <Valaszto label="Iroda" value={aktivId} onChange={(e) => beallit(e.target.value)}>{irodak.map((i) => <option key={i.id} value={i.id}>{i.nev}</option>)}</Valaszto>
    : null;

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[232px_1fr]">
      {/* Asztali sín */}
      <aside className="hidden overflow-hidden border-r border-vonal bg-hatter-melyen p-4 md:sticky md:top-0 md:flex md:h-dvh md:flex-col md:gap-7 md:py-5.5">
        <div className="flex items-center gap-2.5 px-2">
          <img src="/ertekbecslo-logo.png" alt="" width="108" height="130" className="h-[30px] w-auto" />
          <div>
            <b className="block text-sm font-semibold tracking-[-0.01em]">Irodai felület</b>
            <span className="block text-[11px] tracking-[-0.01em] text-szoveg-halk">ingatlanertekmeghatarozas.hu</span>
          </div>
        </div>
        {iroda}
        <nav aria-label="Fő navigáció" className="grid gap-0.5">
          {fo.map((m) => <NavLink key={m.ut} to={m.ut} className={SIN_LINK}>{m.cim}<Jelveny menu={m} /></NavLink>)}
        </nav>
        {uzemeltetes.length > 0 && (
          <nav aria-label="Üzemeltetés" className="grid gap-0.5">
            <span className="px-3 pb-1 text-xs text-szoveg-halk">Üzemeltetés</span>
            {uzemeltetes.map((m) => <NavLink key={m.ut} to={m.ut} className={SIN_LINK}>{m.cim}<Jelveny menu={m} /></NavLink>)}
          </nav>
        )}
        <div className="mt-auto grid gap-2 border-t border-vonal pt-3">
          <HibaUzenet hiba={kilepesHiba} />
          <NavLink to="/profil" className="flex items-center gap-2.5 rounded-[10px] px-2 py-1.5 text-sm hover:bg-felulet">
            <span aria-hidden className="grid size-[30px] shrink-0 place-items-center rounded-full bg-felulet-2 text-xs font-semibold">{monogram(nev)}</span>
            <span className="min-w-0"><span className="block truncate">{nev}</span><span className="block text-xs text-szoveg-halk">{szerep}</span></span>
          </NavLink>
          <Gomb valtozat="szoveg" onClick={kilep}>Kijelentkezés</Gomb>
        </div>
      </aside>

      {/* Mobil felső sáv: márka, irodaválasztó (ha több iroda van) és kilépés */}
      <div className="flex items-end gap-3 border-b border-vonal px-4 py-3 md:hidden">
        <img src="/ertekbecslo-logo.png" alt="" width="108" height="130" className="mb-1 h-8 w-auto" />
        <div className="min-w-0 flex-1">{iroda}</div>
        <Gomb valtozat="szoveg" className="shrink-0 px-3" onClick={kilep}>Kijelentkezés</Gomb>
      </div>
      {kilepesHiba !== null && <div className="px-4 pt-3 md:hidden"><HibaUzenet hiba={kilepesHiba} /></div>}

      <main className="w-full max-w-[1240px] min-w-0 px-4 pt-5 pb-24 md:px-10 md:pt-7 md:pb-14">
        <Outlet />
      </main>

      <HibaGomb />

      {/* Mobil alsó navigáció */}
      <nav aria-label="Mobil navigáció" className="fixed inset-x-0 bottom-0 z-10 grid auto-cols-fr grid-flow-col gap-1 border-t border-vonal bg-hatter-melyen/95 px-1.5 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur md:hidden">
        {mobilFo.map((m) => <NavLink key={m.ut} to={m.ut} className={ALSO_LINK}>{m.cim}</NavLink>)}
        {tobb.length > 0 && <button type="button" className={ALSO_LINK({ isActive: false })} onClick={() => setTobbNyitva(true)}>Több</button>}
        <NavLink to="/profil" className={ALSO_LINK}>Profil</NavLink>
      </nav>
      <Parbeszed nyitva={tobbNyitva} cim="Több" onBezar={() => setTobbNyitva(false)}>
        <nav aria-label="További menüpontok" className="grid gap-1">
          {tobb.map((m) => <NavLink key={m.ut} to={m.ut} onClick={() => setTobbNyitva(false)} className={SIN_LINK}>{m.cim}<Jelveny menu={m} /></NavLink>)}
        </nav>
        <Gomb valtozat="masodlagos" onClick={() => setTobbNyitva(false)}>Bezárás</Gomb>
      </Parbeszed>
    </div>
  );
}
